// Frontend invoice service — thin wrappers around the BFF endpoints.
// Types here are duplicated from bff/src/invoices/schema.ts on purpose: this
// repo does not yet have a shared-types package. If/when the API surface
// stabilises, lift these into one.

import { api } from './api';

export type InvoiceCategory = 'Petrol' | 'Parking' | 'Meal' | 'Other';
export type InvoiceStatus = 'UPLOADED' | 'APPROVED';

export const INVOICE_CATEGORIES: InvoiceCategory[] = ['Petrol', 'Parking', 'Meal', 'Other'];

export const ALLOWED_CONTENT_TYPES = [
  'image/jpeg',
  'image/png',
  'image/heic',
  'image/webp'
];
export const MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024;

export interface Invoice {
  invoiceId: string;
  userId: string;
  userEmail: string;
  userDisplayName: string;
  category: InvoiceCategory;
  otherReason?: string;
  status: InvoiceStatus;
  imageS3Key: string;
  createdAt: string;
  updatedAt: string;
  approvedAt?: string;
  approvedBy?: string;
  isLocked: boolean;
}

export interface InvoiceWithImageUrl extends Invoice {
  imageUrl: string;
  imageUrlExpiresInSeconds: number;
}

export interface CreateInvoiceResult {
  invoiceId: string;
  uploadUrl: string;
  imageS3Key: string;
  expiresInSeconds: number;
}

export interface InvoicePage {
  invoices: Invoice[];
  cursor?: string;
}

export interface AdminInvoicePage {
  invoices: InvoiceWithImageUrl[];
  cursor?: string;
}

// ----- Staff -----

export function listMyInvoices(opts: { limit?: number; cursor?: string } = {}): Promise<InvoicePage> {
  const qs = new URLSearchParams();
  if (opts.limit) qs.set('limit', String(opts.limit));
  if (opts.cursor) qs.set('cursor', opts.cursor);
  const tail = qs.toString() ? `?${qs}` : '';
  return api<InvoicePage>(`/api/invoices${tail}`);
}

export function getInvoice(invoiceId: string): Promise<InvoiceWithImageUrl> {
  return api<InvoiceWithImageUrl>(`/api/invoices/${encodeURIComponent(invoiceId)}`);
}

export function createInvoice(input: {
  category: InvoiceCategory;
  otherReason?: string;
  fileName: string;
  contentType: string;
}): Promise<CreateInvoiceResult> {
  return api<CreateInvoiceResult>('/api/invoices', {
    method: 'POST',
    body: JSON.stringify(input)
  });
}

export function updateInvoice(invoiceId: string, patch: { category?: InvoiceCategory; otherReason?: string }): Promise<Invoice> {
  return api<Invoice>(`/api/invoices/${encodeURIComponent(invoiceId)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch)
  });
}

export function replaceInvoiceImage(invoiceId: string, contentType: string): Promise<{
  invoice: Invoice;
  uploadUrl: string;
  imageS3Key: string;
  expiresInSeconds: number;
}> {
  return api(`/api/invoices/${encodeURIComponent(invoiceId)}/replace-image`, {
    method: 'POST',
    body: JSON.stringify({ contentType })
  });
}

/**
 * PUT a binary blob to an S3 presigned URL. The browser does this directly;
 * the BFF never sees the bytes.
 */
export async function uploadToS3(uploadUrl: string, file: File): Promise<void> {
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`S3 upload failed: ${res.status} ${text}`);
  }
}

// ----- Admin -----

export function listAllInvoices(query: {
  status?: InvoiceStatus;
  category?: string;
  userId?: string;
  from?: string;
  to?: string;
  cursor?: string;
  limit?: number;
} = {}): Promise<AdminInvoicePage> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== '') qs.set(k, String(v));
  }
  const tail = qs.toString() ? `?${qs}` : '';
  return api<AdminInvoicePage>(`/api/admin/invoices${tail}`);
}

export function approveInvoice(invoiceId: string): Promise<Invoice> {
  return api<Invoice>(`/api/admin/invoices/${encodeURIComponent(invoiceId)}/approve`, {
    method: 'POST'
  });
}

// ----- UI helpers -----

export function formatTimestamp(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}
